/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import { Button, SkeletonText, Loading } from '@carbon/react';
import _ from 'lodash';
import React, { useEffect, useState, useCallback } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { useMount } from 'react-use';

import { ReadOnlyNotification } from '@ibm-watson/shared/modules/ia/components/access-control/ReadOnlyNotification';
import { CancelConfirmationModal } from '@ibm-watson/shared/modules/ia/components/cancel-confirmation-modal/CancelConfirmationModal';
import { EnvironmentTag } from '@ibm-watson/shared/modules/ia/components/environment-tag/EnvironmentTag';
import Panel from '@ibm-watson/shared/modules/ia/components/panel/Panel';
import { isEnvironmentReadOnly } from '@ibm-watson/shared/modules/ia/store/access-control/selectors';
import {
  ctaEvents,
  pageEvents
} from '@ibm-watson/shared/modules/ia/util/trackers/segment';
import { useAppDispatch, useAppSelector } from '@ibm-watson/shared/store';
import { LoaderStates } from '../../../../ts';
import { useIsNewIA } from '../../../ia/hooks/useIsNewIA';
import { selectIsGenesysAudioConnectorInCpd } from '../../../ia/store/features/selectors';
import * as integrationServerApi from '../../api/integrations/integrationServer';
import {
  useChannelOverlay,
  ChannelOverlayProvider
} from '../../hooks/useChannelOverlayContext';
import { listCatalogItems } from '../../store/catalog/actions';
import { isSegmentCatalogItemId } from '../../store/catalog/selectors';
import {
  createIntegration,
  deleteIntegration,
  listIntegrations,
  resetCurrentIntegration,
  updateIntegration
} from '../../store/channels/actions';
import {
  areIntegrationsLoading,
  didSubscribeIntegrationFail,
  isSubscribeIntegrationLoading,
  selectDraftIntegration,
  selectFirstIntegrationIdByType,
  selectIntegrationHasChanges,
  selectIsIntegrationDirty,
  selectIsIntegrationInitializing,
  selectIsIntegrationPrematurelyCreated,
  selectIsSaveCreateButtonDisabled,
  selectSavedDraftIntegration,
  selectShouldSaveIntegration
} from '../../store/channels/selectors';
import { resetContactCenter } from '../../store/phone/actions';
import { selectIntelepeerState } from '../../store/phone/selectors';
import { resetServiceDesk } from '../../store/service-desks/actions';
import {
  selectDraftServiceDesk,
  selectShouldSaveServiceDesk
} from '../../store/service-desks/selectors';
import {
  resetSpeechToTextInstance,
  resetTextToSpeechInstance
} from '../../store/voice/actions';
import {
  selectCurrentSpeechToTextInstance,
  selectCurrentTextToSpeechInstance
} from '../../store/voice/selectors';
import {
  channelTypeToChannelName,
  CATALOG_CHANNEL_TYPE,
  FACEBOOK_CHANNEL_TYPE,
  PHONE_CHANNEL_TYPE,
  SLACK_CHANNEL_TYPE,
  TEAMS_CHANNEL_TYPE,
  WEBCHAT_CHANNEL_TYPE,
  WHATSAPP_CHANNEL_TYPE,
  GENESYS_BOT_CONNECTOR_CHANNEL_TYPE,
  FREE_PHONE_BYOSD_TYPE,
  SEGMENT_EXTENSION_TYPE,
  SMS_CHANNEL_TYPES
} from '../../util/channelTypes';
import { objectDifference } from '../../util/helpers';
import { CatalogOpenApi } from '../catalog-openapi/CatalogOpenApi';
import Facebook from '../facebook/Facebook';
import GenesysBotConnector from '../genesys-bot-connector/GenesysBotConnector';
import { sharedMessages } from '../messages';
import MicrosoftTeams from '../microsoft-teams/MicrosoftTeams';
import { NARROWBAND, TELEPHONY } from '../phone/constants';
import { Phone } from '../phone/Phone';
import { removeAutoProvisionedNumber } from '../phone/phone-helpers';
import { FIELDS as PHONE_FIELDS } from '../phone/schema';
import { Segment } from '../segment/Segment';
import Slack from '../slack/Slack';
import { SMS } from '../sms/SMS';
import { WebChat } from '../web-chat/WebChat';
import { WhatsApp } from '../whatsapp/WhatsApp';

import './ChannelOverlay.scss';

interface ChannelOverlayProps {
  /**
   *
   */
  assistantId: string;
  /**
   *
   */
  environmentId: string;
  /**
   *
   */
  channelType: string;
  /**
   *
   */
  catalogItemId?: string;
  /**
   *
   */
  onClose: () => void;
  /**
   *
   */
  defaultTab?: number;
}
export const ChannelOverlay = ({
  assistantId,
  environmentId,
  channelType,
  catalogItemId,
  onClose,
  defaultTab = 0
}: ChannelOverlayProps) => {
  const dispatch = useAppDispatch();
  const { formatMessage: t } = useIntl();

  const channelOverlayContext = useChannelOverlay();
  const { handleNext, finished, enableNext, showNextBack } =
    channelOverlayContext;
  const isSMSChannelType = SMS_CHANNEL_TYPES.includes(channelType);

  // SELECTORS
  const isLoading = useAppSelector(areIntegrationsLoading);
  const isSaveCreateButtonDisabled = useAppSelector(
    selectIsSaveCreateButtonDisabled
  );
  const integrationShouldSave = useAppSelector(selectShouldSaveIntegration);
  const serviceDeskShouldSave = useAppSelector(selectShouldSaveServiceDesk);
  const savedDraftIntegration = useAppSelector(selectSavedDraftIntegration);
  const draftIntegration = useAppSelector(selectDraftIntegration);
  const draftServiceDesk = useAppSelector(selectDraftServiceDesk);
  const integrationId = useAppSelector(state =>
    selectFirstIntegrationIdByType(
      state,
      channelType,
      environmentId,
      catalogItemId
    )
  );
  const isIntegrationDirty = useAppSelector(selectIsIntegrationDirty);
  const isIntegrationPrematurelyCreated = useAppSelector(
    selectIsIntegrationPrematurelyCreated
  );

  const isEnvReadOnly = useAppSelector(state =>
    isEnvironmentReadOnly(state, environmentId)
  );

  const isSegment = useAppSelector(state =>
    isSegmentCatalogItemId(state, catalogItemId)
  );
  const subscribeLoading = useAppSelector(isSubscribeIntegrationLoading);
  const subscribeFailed = useAppSelector(didSubscribeIntegrationFail);
  const integrationHasChanges = useAppSelector(selectIntegrationHasChanges);
  const intelepeerNumState = useAppSelector(selectIntelepeerState);
  const initializingSpeech = useAppSelector(selectIsIntegrationInitializing);
  const currentSTTInstance = useAppSelector(selectCurrentSpeechToTextInstance);
  const currentTTSInstance = useAppSelector(selectCurrentTextToSpeechInstance);
  const isGenesysAudioConnectorEnabledInCpd = useAppSelector(
    selectIsGenesysAudioConnectorInCpd
  );

  // STATES
  const [isCancelModalOpen, setIsCancelModalOpen] = useState<boolean>(false);
  // Setting this to true keeps loading of channels from accidentally
  // occurring while closing the ChannelOverlay.
  const [closing, setClosing] = useState<boolean>(false);

  const [isSaveEnabled, setIsSaveEnabled] = useState<boolean>(
    !isEnvReadOnly &&
      !isSaveCreateButtonDisabled &&
      !initializingSpeech &&
      (integrationShouldSave || serviceDeskShouldSave)
  );

  // CONVENIENCE VARIABLES
  const integrationName = isSegment
    ? channelTypeToChannelName[SEGMENT_EXTENSION_TYPE]
    : isSMSChannelType
    ? 'SMS'
    : channelTypeToChannelName[channelType];

  const isDirtyIntelepeer =
    savedDraftIntegration?.settings?.contact_center?.provider !==
      FREE_PHONE_BYOSD_TYPE &&
    draftIntegration?.settings?.contact_center?.provider ===
      FREE_PHONE_BYOSD_TYPE;

  // Cannot delete connection / unsubscribe when an intelepeer number is provisioning
  const isDirtyProvisioningIntelepeerNum =
    channelType === PHONE_CHANNEL_TYPE && isDirtyIntelepeer
      ? intelepeerNumState === LoaderStates.ACTIVE &&
        !draftIntegration?.settings?.phone_numbers[0]?.provision_success
      : false;

  /**
   * Enable the Save and Exit button for webchat if not read-only plus either
   * webchat or servicedesk should save. Otherwise, check for if environment
   * isn't read-only and the instance should save. Should not enable save and exit
   * if a new phone integration's speech is still initializing. This is set on a 100ms delay
   * to prevent flases.
   */
  useEffect(() => {
    const saveButtonDelay = setTimeout(() => {
      const isSaveEnabled =
        !isEnvReadOnly &&
        !isSaveCreateButtonDisabled &&
        !initializingSpeech &&
        (integrationShouldSave || serviceDeskShouldSave);
      setIsSaveEnabled(isSaveEnabled);
    }, 100);
    return () => {
      clearTimeout(saveButtonDelay);
    };
  }, [
    initializingSpeech,
    integrationShouldSave,
    isEnvReadOnly,
    isSaveCreateButtonDisabled,
    serviceDeskShouldSave
  ]);
  // The variables below are used to prevent of the flickering of an enabled
  // 'Create' button in the integration header when a new phone integration is
  // added to an assistant in New IA
  const isNewIA = useIsNewIA();
  const isPhoneNewIA = isNewIA && channelType === PHONE_CHANNEL_TYPE;
  const isNewPhoneInNewIA = _.isNil(integrationId) && isPhoneNewIA;

  /**
   * Trigger an 'integrationEdit' segment event once we load an integration to edit.
   */
  useMount(() => {
    if (integrationId) {
      pageEvents.integrationEdit(null, {
        waIntegrationType: draftIntegration?.type,
        waIntegrationId: integrationId,
        waEnvironmentId: environmentId
      });
    }
  });

  /**
   *
   */
  useEffect(() => {
    dispatch(listIntegrations(assistantId));
    if (channelType === CATALOG_CHANNEL_TYPE) {
      dispatch(listCatalogItems(assistantId));
    }
  }, [assistantId, dispatch, environmentId, channelType]);

  /**
   *
   */
  const phoneExitedSegmentEvent = useCallback(() => {
    if (draftIntegration?.type === PHONE_CHANNEL_TYPE) {
      const bandPreference = _.get(
        draftIntegration.settings,
        PHONE_FIELDS.STT.BAND_PREFERENCE
      );
      const model = [NARROWBAND, TELEPHONY].includes(bandPreference)
        ? _.get(draftIntegration.settings, PHONE_FIELDS.STT.NARROWBAND.MODEL)
        : _.get(draftIntegration.settings, PHONE_FIELDS.STT.BROADBAND.MODEL);
      const voice = _.get(draftIntegration.settings, PHONE_FIELDS.TTS.VOICE);

      ctaEvents.phoneIntegrationExited(null, {
        waSTTModel: model,
        waTTSVoice: voice
      });
    }
  }, [draftIntegration]);

  /**
   * Handle when the user clicks on the Close button. If the
   * integration was dirty or prematurely created, open the
   * Cancel Confirmation Modal asking if the user really wants to
   * exit.
   */
  const handleCloseButton = useCallback(async () => {
    // If the integration is created on the last step for webhook creation
    // but the webhook subscribe fails, we should make the onClose button
    // delete the integration and remove the integration_id
    if (
      showNextBack &&
      finished &&
      isIntegrationPrematurelyCreated &&
      !_.isNil(savedDraftIntegration)
    ) {
      setClosing(true);
      await dispatch(
        deleteIntegration({
          assistantId,
          environmentId,
          integration: savedDraftIntegration
        })
      );
      await dispatch(resetServiceDesk());
      await dispatch(resetCurrentIntegration());
      onClose();
    } else if (
      integrationHasChanges &&
      (isIntegrationDirty || isIntegrationPrematurelyCreated)
    ) {
      setIsCancelModalOpen(true);
    } else {
      phoneExitedSegmentEvent();
      onClose();
    }
  }, [
    assistantId,
    dispatch,
    environmentId,
    finished,
    integrationHasChanges,
    isIntegrationDirty,
    isIntegrationPrematurelyCreated,
    onClose,
    phoneExitedSegmentEvent,
    savedDraftIntegration,
    showNextBack
  ]);

  /**
   * When the user clicks on Discard Changes in the Cancel Confirmation
   * Modal, we first need to check whether we have a prematurely
   * created integration we have to delete.
   */
  const handleDiscardChanges = useCallback(async () => {
    // This weird logic resolves the case where the channel IS
    // REQUIRED TO create an integration BEFORE the setup is fully
    // completed. If a channel creates an integration mid-setup
    // (e.g. integration calls subscribe during quick setup),
    // createIntegration action populates the integration into
    // redux entities, and isIntegrationPrematurelyCreated is set to true.
    // Therefore (isIntegrationPrematurelyCreated && savedDraftIntegration)
    // effectively checks whether a channel component has created
    // the integration in the middle of the setup process.
    if (isIntegrationPrematurelyCreated && !_.isNil(savedDraftIntegration)) {
      await dispatch(
        deleteIntegration({
          assistantId,
          environmentId,
          integration: savedDraftIntegration
        })
      );
    }

    // If the integration is a phone integration and the recent
    // changes include a newly provisioned number, then we must
    // unsubscribe and remove the auto-provisioned number.
    const dirtyFields = objectDifference(
      savedDraftIntegration,
      draftIntegration?.settings
    );
    const contactCenterProvider = _.get(
      dirtyFields,
      PHONE_FIELDS.CONTACT_CENTER.PROVIDER
    );
    if (contactCenterProvider === FREE_PHONE_BYOSD_TYPE) {
      // unset the contact center type
      _.set(
        draftIntegration?.settings,
        PHONE_FIELDS.CONTACT_CENTER.PROVIDER,
        ''
      );
      // unsubscribe the phone integration
      await integrationServerApi.unsubscribe(
        PHONE_CHANNEL_TYPE,
        draftIntegration?.integration_id
      );
      dispatch(resetContactCenter());
      // look for a provisioned number, and if it exists, remove
      // it from the draft integration and post the update
      // integration call
      const isProvisionedNumberRemoved = await removeAutoProvisionedNumber(
        _.get(draftIntegration?.settings, PHONE_FIELDS.PHONE_NUMBERS)
      );
      if (isProvisionedNumberRemoved) {
        await dispatch(
          updateIntegration({
            assistantId,
            environmentId,
            integration: draftIntegration
          })
        );
      }
    }

    await dispatch(resetServiceDesk());
    await dispatch(resetCurrentIntegration());
    await dispatch(resetSpeechToTextInstance());
    await dispatch(resetTextToSpeechInstance());
    onClose();
  }, [
    assistantId,
    dispatch,
    draftIntegration,
    environmentId,
    isIntegrationPrematurelyCreated,
    onClose,
    savedDraftIntegration
  ]);

  /**
   * Save or update the service desk if there is one.
   */
  const saveServiceDeskIfNeeded = useCallback(async () => {
    if (
      serviceDeskShouldSave &&
      draftIntegration?.type === WEBCHAT_CHANNEL_TYPE &&
      !_.isNil(draftServiceDesk)
    ) {
      // Service desk already exists, so an update is needed
      if (!_.isNil(draftServiceDesk?.integration_id)) {
        await dispatch(
          updateIntegration({
            assistantId,
            environmentId,
            integration: draftServiceDesk
          })
        );
      } else {
        // New service desk, so a creation is needed
        await dispatch(
          createIntegration({
            assistantId,
            environmentId,
            integration: draftServiceDesk
          })
        );
      }
    }
  }, [
    assistantId,
    dispatch,
    draftIntegration?.type,
    draftServiceDesk,
    environmentId,
    serviceDeskShouldSave
  ]);

  /**
   * Save or update the integration in New IA depending on whether the
   * integration previously exists. Also triggers saving the service
   * desk.
   */
  const saveIntegration = useCallback(async () => {
    await saveServiceDeskIfNeeded();

    // When a phone integration is being edited, and private.secrets
    // fields other than STT or TTS are changed, the lack of STT/TTS
    // secrets fields means that Store will overwrite these fields
    // today. To solve this, we take the current STT+TTS instances
    // and use them to resupply the id, url, and apikey inot the
    // integration to save/update.
    // TO DO: Please delete this piece of code once Store implements
    // partial updates of private.secrets fields.
    const modifiedIntegration = _.cloneDeep(draftIntegration);

    // Omits tenant id key if it is empty for teams integration
    if (
      channelType == TEAMS_CHANNEL_TYPE &&
      'tenant_id' in draftIntegration?.settings?.private &&
      !draftIntegration?.settings?.private?.tenant_id
    ) {
      delete modifiedIntegration.settings.private['tenant_id'];
    }

    // if phone integration and not genesys connector enabled in CPD
    if (
      modifiedIntegration?.type === PHONE_CHANNEL_TYPE &&
      !isGenesysAudioConnectorEnabledInCpd
    ) {
      // If a STT instance was selected, use the current instance's
      // id, url, and apikey to save or update the integration.
      if (!_.isEmpty(currentSTTInstance)) {
        _.set(
          modifiedIntegration?.settings,
          PHONE_FIELDS.STT.INSTANCE_ID,
          currentSTTInstance?.id
        );
        _.set(
          modifiedIntegration?.settings,
          PHONE_FIELDS.STT.URL,
          currentSTTInstance?.credentials?.url
        );
        _.set(
          modifiedIntegration?.settings,
          PHONE_FIELDS.STT.APIKEY,
          currentSTTInstance?.credentials?.apikey
        );
      }
      // If a TTS instance was selected, use the current instance's
      // id, url, and apikey to save or update the integration.
      if (!_.isEmpty(currentTTSInstance)) {
        _.set(
          modifiedIntegration?.settings,
          PHONE_FIELDS.TTS.INSTANCE_ID,
          currentTTSInstance?.id
        );
        _.set(
          modifiedIntegration?.settings,
          PHONE_FIELDS.TTS.URL,
          currentTTSInstance?.credentials?.url
        );
        _.set(
          modifiedIntegration?.settings,
          PHONE_FIELDS.TTS.APIKEY,
          currentTTSInstance?.credentials?.apikey
        );
      }
    }

    if (integrationShouldSave) {
      // Integration already exists, so an update is needed
      if (!_.isNil(savedDraftIntegration)) {
        await dispatch(
          updateIntegration({
            assistantId,
            environmentId,
            integration: modifiedIntegration
          })
        );
      } else if (!isIntegrationPrematurelyCreated) {
        // New integration, so a creation is needed
        await dispatch(
          createIntegration({
            assistantId,
            environmentId,
            integration: modifiedIntegration
          })
        );
      }
    }
  }, [
    assistantId,
    channelType,
    currentSTTInstance,
    currentTTSInstance,
    dispatch,
    draftIntegration,
    environmentId,
    integrationShouldSave,
    isIntegrationPrematurelyCreated,
    savedDraftIntegration,
    saveServiceDeskIfNeeded,
    isGenesysAudioConnectorEnabledInCpd
  ]);

  /**
   * Save the integration (IF the integration wasn't previously and
   * prematurely created), then close this ChannelOverlay.
   */
  const saveAndCloseOverlay = useCallback(async () => {
    setClosing(true);
    phoneExitedSegmentEvent();

    await saveIntegration();

    dispatch(resetServiceDesk());
    dispatch(resetContactCenter());
    dispatch(resetCurrentIntegration());
    dispatch(resetSpeechToTextInstance());
    dispatch(resetTextToSpeechInstance());
    onClose();
  }, [dispatch, onClose, phoneExitedSegmentEvent, saveIntegration, setClosing]);

  /**
   * Handle when the user clicks on the Next or the Save button.
   * If the user clicks on Save, then trigger the logic for saving
   * the integration and closing the overlay.
   */
  const handleSubmit = useCallback(async () => {
    // If there is no previous/next button,
    // then clicking the primary button means saving.
    // If the form is finished,
    // then clicking the primary button means saving.
    if (!showNextBack || finished) {
      await saveAndCloseOverlay();
    } else {
      handleNext();
    }
  }, [finished, handleNext, saveAndCloseOverlay, showNextBack]);

  /**
   * Returns a rendered UI component specific to the channel type.
   */
  const renderChannel = () => {
    if (isLoading || closing) {
      return <Loading withOverlay />;
    }

    if (channelType === WEBCHAT_CHANNEL_TYPE) {
      return (
        <WebChat
          assistantId={assistantId}
          environmentId={environmentId}
          integrationId={integrationId}
          defaultTab={defaultTab}
          onClose={handleCloseButton}
        />
      );
    } else if (channelType === WHATSAPP_CHANNEL_TYPE) {
      return (
        <WhatsApp
          assistantId={assistantId}
          environmentId={environmentId}
          integrationId={integrationId}
        />
      );
    } else if (isSMSChannelType) {
      return (
        <SMS
          assistantId={assistantId}
          environmentId={environmentId}
          integrationId={integrationId}
        />
      );
    } else if (channelType === PHONE_CHANNEL_TYPE) {
      return (
        <Phone
          assistantId={assistantId}
          environmentId={environmentId}
          integrationId={integrationId}
        />
      );
    } else if (channelType === FACEBOOK_CHANNEL_TYPE) {
      return (
        <Facebook
          assistantId={assistantId}
          environmentId={environmentId}
          integrationId={integrationId}
        />
      );
    } else if (channelType === SLACK_CHANNEL_TYPE) {
      return (
        <Slack
          assistantId={assistantId}
          environmentId={environmentId}
          integrationId={integrationId}
        />
      );
    } else if (channelType === GENESYS_BOT_CONNECTOR_CHANNEL_TYPE) {
      return (
        <GenesysBotConnector
          assistantId={assistantId}
          environmentId={environmentId}
          integrationId={integrationId}
        />
      );
    } else if (channelType === TEAMS_CHANNEL_TYPE) {
      return (
        <MicrosoftTeams
          assistantId={assistantId}
          environmentId={environmentId}
          integrationId={integrationId}
        />
      );
    } else if (channelType === CATALOG_CHANNEL_TYPE && !isSegment) {
      return (
        <CatalogOpenApi
          assistantId={assistantId}
          environmentId={environmentId}
          integrationId={integrationId}
          catalogItemId={catalogItemId}
          defaultTab={defaultTab}
        />
      );
    } else if (isSegment) {
      return (
        <Segment
          assistantId={assistantId}
          environmentId={environmentId}
          integrationId={integrationId}
        />
      );
    }

    return (
      <FormattedMessage
        {...sharedMessages.notSupported}
        values={{ integrationName }}
      />
    );
  };

  return (
    <>
      <ChannelOverlayProvider {...channelOverlayContext}>
        <Panel
          className="ChannelOverlay"
          title={
            !_.isNil(integrationId)
              ? t(sharedMessages.overlayEditTitle)
              : t(sharedMessages.overlayCreateTitle)
          }>
          <header
            className="ChannelOverlay__header"
            aria-labelledby="integration-panel-heading">
            <div className="ChannelOverlay__header--name">
              <h2 id="integration-panel-heading">
                {integrationName || <SkeletonText heading width="250px" />}
              </h2>
            </div>
            <div className="ChannelOverlay__header--tag">
              <EnvironmentTag environmentId={environmentId} />
            </div>
            <div className="ChannelOverlay__header--actions">
              <Button
                kind="secondary"
                onClick={handleCloseButton}
                disabled={isDirtyProvisioningIntelepeerNum}
                id="closePanel">
                {t(sharedMessages.close)}
              </Button>
              {showNextBack ? (
                <>
                  <Button
                    className="ChannelOverlay__next-finish-button"
                    kind="primary"
                    onClick={handleSubmit}
                    disabled={
                      finished
                        ? !isSaveEnabled || subscribeLoading || subscribeFailed
                        : !enableNext
                    }>
                    {showNextBack && finished
                      ? t(sharedMessages.finish)
                      : t(sharedMessages.next)}
                  </Button>
                </>
              ) : (
                <Button
                  kind="primary"
                  onClick={handleSubmit}
                  disabled={!isSaveEnabled || isNewPhoneInNewIA}>
                  {!_.isNil(integrationId) || isPhoneNewIA
                    ? t(sharedMessages.saveAndExit)
                    : t(sharedMessages.create)}
                </Button>
              )}
            </div>
          </header>
          {isEnvReadOnly && (
            <ReadOnlyNotification environmentId={environmentId} />
          )}
          <div className="ChannelOverlay__content">{renderChannel()}</div>
        </Panel>
        <CancelConfirmationModal
          modalBodyText={t(sharedMessages.saveEditBody)}
          isOpen={isCancelModalOpen}
          onCancel={() => setIsCancelModalOpen(false)}
          onClose={handleDiscardChanges}
          onSubmit={saveAndCloseOverlay}
          primaryButtonDisabled={!isSaveEnabled}
        />
      </ChannelOverlayProvider>
    </>
  );
};
